import React, { useState } from 'react';
import { Box, Typography, Grid } from '@availity/element';
import LogoGrid from '../HomePage/LogoGrid';
import Button from '../Utils/components/Button';
import EarlyAccessForm, { FormData } from './EarlyAccessForm';
import { ACCORDION_CONTENT, WHY_JOIN_ITEMS } from './constants';
import joinEarlyPageStyles from './joinEarlyPageStyles';
import earlyAccessRightPanel from '../../static/assets/illustrations/Early-access-right-panel.svg';
import successIcon from '../../static/assets/icons/early-access-success.svg';
import angleDownIcon from '../../static/assets/icons/angle-down.svg';
import angleRightIcon from '../../static/assets/icons/angle-right.svg';
import postEarlyAccess from '../../api/earlyAccessApi';
import { trackEvent } from '../Utils/splunkEventHelper';

const JoinEarlyPage = () => {
  const {
    heroSection,
    decorationImage,
    heroContent,
    heroTitle,
    heroSubtitle,
    mainContent,
    leftColumn,
    whyJoinContainer,
    whyJoinTitle,
    whyJoinItem,
    whyJoinItemTitle,
    whyJoinItemText,
    accordionContainer,
    accordionBox,
    accordionHeader,
    accordionTitle,
    accordionContent,
    accordionItemText,
    formContainer,
    successContainer,
    successTitle,
    successText,
  } = joinEarlyPageStyles;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(null);

  const handleFormSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      await postEarlyAccess(data);
      setSubmitStatus('success');
    } catch (error) {
      trackEvent({
        event: 'early-access-api-request-failure',
        status: (error as { response?: { status?: number } })?.response?.status,
        api: 'early-access-api',
      });
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleAccordion = (id: string) => {
    setExpandedAccordion((prev) => (prev === id ? null : id));
  };

  return (
    <Grid>
      <Box sx={heroSection}>
        <Box sx={decorationImage}>
          <img src={earlyAccessRightPanel} alt="Early Access Decoration" style={{ width: '500px', height: 'auto' }} />
        </Box>

        <Box sx={heroContent}>
          <Typography sx={heroSubtitle}>
            Early Adoption Program
          </Typography>
          <Typography sx={heroTitle}>Be First to Build What&apos;s Next</Typography>
        </Box>

        <Box sx={mainContent}>
          <Box sx={leftColumn}>
            <Box sx={whyJoinContainer}>
              <Typography sx={whyJoinTitle}>Why Join?</Typography>
              {WHY_JOIN_ITEMS.map(([title, text]) => (
                <Box key={title} sx={whyJoinItem}>
                  <Typography sx={whyJoinItemTitle}>{title}</Typography>
                  <Typography sx={whyJoinItemText}>{text}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={accordionContainer}>
              {Object.entries(ACCORDION_CONTENT).map(([id, items]) => {
                const isExpanded = expandedAccordion === id;
                return (
                  <Box key={id} sx={accordionBox}>
                    <Box onClick={() => toggleAccordion(id)} sx={accordionHeader}>
                      <img src={isExpanded ? angleDownIcon : angleRightIcon} alt="" style={{ height: 16, width: 16, filter: 'brightness(0) saturate(100%) invert(64%) sepia(48%) saturate(1234%) hue-rotate(179deg) brightness(95%) contrast(89%)', transition: 'transform 0.3s' }} />
                      <Typography sx={accordionTitle}>{id}</Typography>
                    </Box>
                    {isExpanded && (
                      <Box sx={accordionContent}>
                        {items.map((item) => (
                          <Typography key={item} sx={accordionItemText}>
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box sx={formContainer}>
            {submitStatus === 'success' ? (
              <Box sx={successContainer}>
                <img src={successIcon} alt="Success" style={{ width: 120, height: 120 }} />
                <Typography sx={successTitle}>Thank you for signing up for Early access program!</Typography>
                <Typography sx={successText}>We&apos;ll review your request and get back within 3–5 business days</Typography>
                <Button onClick={() => { window.location.href = '/docs/overview'; }}>Explore APIs</Button>
              </Box>
            ) : (
              <EarlyAccessForm
                onSubmit={handleFormSubmit}
                isSubmitting={isSubmitting}
                submitError={submitStatus === 'error'}
              />
            )}
          </Box>
        </Box>
      </Box>
      <LogoGrid />
    </Grid>
  );
};

export default JoinEarlyPage;
